import React, { useEffect, useState, useRef, FC } from 'react'
import Form from 'components/Form'
import { Formik } from 'formik'
import { IconButton } from 'components/Button'
import { isMac, tone, breakpoint } from 'utils'
import styled from 'styled-components'
import CommentInputIfSignedOut from './CommentInputIfSignedOut'
import SuggestionsInput, { markdown } from 'components/SuggestionsInput'
import { Flex } from 'components/Box'
import { ReactComponent as SendMessageIcon } from 'icons/send-message-26.svg'
import { ReactComponent as AtIcon } from 'icons/at-sign-20.svg'
import { ReactComponent as EmojiIcon } from 'icons/emoji-20.svg'
import Tooltip from 'components/Tooltip'
import SupportsTouch from 'components/SupportsTouch'
import Breakpoint from 'components/Breakpoint'
import { useGetArtboardMentionableUsersQuery } from 'gqlTypes'
import theme from 'styles/theme'
import { useCreateComment } from '../operations/useCreateComment'
import { ArtboardInSidebar, ShareInSidebar } from 'modules/shares/types'

// Types
interface CommentInputProps {
  style?: React.CSSProperties
  commentIdentifier: string | undefined
  artboardIdentifier: ArtboardInSidebar['identifier']
  permanentArtboardShortId: ArtboardInSidebar['permanentArtboardShortId']
  documentVersionShortId: ArtboardInSidebar['documentVersionShortId']
  shareIdentifier: ShareInSidebar['identifier']
  isSignedIn: boolean
}

// Styles
const SendButton = styled(IconButton).attrs({ small: true })`
  padding-left: 1rem; /* stylelint-disable-line scales/space */
  padding-right: 1rem; /* stylelint-disable-line scales/space */
  color: ${({ theme }) => theme.colors.primaryTint[100]};

  :disabled {
    color: ${({ theme }) => theme.colors.gray};
    opacity: 0.5;
  }

  &:hover,
  &:focus {
    color: ${({ theme }) => tone(-2, theme.colors.primaryTint[100])};
  }
`

const StyledForm = styled(Form)`
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.colors.border.dark.base};
  background-color: ${({ theme }) => theme.colors.background.light.base};
`

const suggestionsStyle = {
  suggestions: {
    top: 'auto',
    left: 0,
    right: 0,
    list: {
      paddingTop: '10px',
      paddingBottom: '10px',
    },
  },
}

// Helpers
export const shortcutSuggestionTooltipContent = () =>
  isMac() ? 'Command-Enter' : 'Control-Enter'

const TriggerButton = styled(IconButton)`
  color: ${({ theme }) => theme.colors.gray};

  :hover {
    color: ${({ theme }) => theme.colors.woodsmoke};
  }

  ${breakpoint('base', 'sm')`
    padding: 12px 16px 12px 8px;
  `};
`

// Components
export const CommentInput: FC<CommentInputProps> = ({
  style = {},
  artboardIdentifier,
  permanentArtboardShortId,
  documentVersionShortId,
  commentIdentifier,
  isSignedIn,
  shareIdentifier,
}) => {
  // Operations
  const [createComment] = useCreateComment({
    permanentArtboardShortId,
    documentVersionShortId,
    artboardIdentifier,
    commentIdentifier,
    shareIdentifier,
  })

  const [commentBody, setCommentBody] = useState('')

  // Focus on the comment input when thread panel loads
  const inputRef = useRef<
    HTMLInputElement & { selectionStart: number; selectionEnd: number }
  >()
  useEffect(() => {
    if (commentIdentifier) {
      // Ensure that the input is mounted before setting focus
      setTimeout(() => {
        inputRef?.current?.focus()
      })
    }
  }, [commentIdentifier])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCommentBody(event.target.value)
  }

  const handleSuggestionsKeyDown = (event: KeyboardEvent) => {
    const auxKey = event.metaKey || event.ctrlKey

    const markDownChange = markdown.getChangeByEventKey(event.key)
    if (auxKey && markDownChange) {
      event.preventDefault()

      if (inputRef.current) {
        const { selectionStart, selectionEnd } = inputRef.current

        const { text, ...selection } = markdown.toggleTextWithChange(
          commentBody,
          markDownChange,
          selectionStart,
          selectionEnd
        )

        setCommentBody(text)
        inputRef.current.setSelectionRange(
          selection.selectionStart,
          selection.selectionEnd
        )
      }
      return
    }
  }

  const resetForm = () => {
    setCommentBody('')
  }

  const insertEmojiTrigger = () => {
    // Insert extra space after the current value except if the current value is empty
    setCommentBody(commentBody.length === 0 ? ':' : commentBody + ' :')

    inputRef?.current?.focus()
  }

  const insertMentionTrigger = () => {
    // Insert extra space after the current value except if the current value is empty
    setCommentBody(commentBody.length === 0 ? '@' : commentBody + ' @')

    inputRef?.current?.focus()
  }

  const handleSubmit = () => {
    if (!commentBody) return
    createComment({ body: commentBody })
    resetForm()
  }

  const { data } = useGetArtboardMentionableUsersQuery({
    variables: { permanentArtboardShortId, shareIdentifier },
    fetchPolicy: 'network-only',
    skip: !isSignedIn,
  })

  const mentionableUsers = data?.artboard?.mentionableUsers.map(user => ({
    // TODO: mark mentionableUsers as non_null on GraphQL schema
    // https://github.com/sketch-hq/Cloud/issues/4828
    id: user!.identifier,
    display: user!.name,
    avatar: user!.avatar!.small,
  }))

  if (!isSignedIn) return <CommentInputIfSignedOut />

  return (
    <Formik initialValues={{}} onSubmit={handleSubmit}>
      <StyledForm style={style} data-testid="comment-input">
        <SuggestionsInput
          inputRef={inputRef}
          value={commentBody}
          mentionableUsers={mentionableUsers}
          onSubmit={handleSubmit}
          onChange={handleChange}
          onKeyDown={handleSuggestionsKeyDown}
          style={suggestionsStyle}
          placeholder={
            commentIdentifier ? 'Write a reply...' : 'Write a comment...'
          }
        />
        <Flex justifyContent="space-between" alignItems="center" mb={3} pl={5}>
          <Flex justifyContent="flex-start">
            {/*
              We shouldn't show the emoji icon in mobile, since most phone keyboards
              have emoji support build in.

              https://github.com/sketch-hq/Cloud/issues/2004#issuecomment-539848866
            */}
            <Breakpoint on="sm">
              {matches =>
                matches && (
                  <TriggerButton
                    type="button"
                    icon={EmojiIcon}
                    size="20px"
                    onClick={insertEmojiTrigger}
                    p={`0 5px 0 ${theme.space[2]}px`}
                    data-testid="emoji-icon"
                  />
                )
              }
            </Breakpoint>

            <TriggerButton
              type="button"
              icon={AtIcon}
              size="20px"
              onClick={insertMentionTrigger}
              p={`0 ${theme.space[2]}px 0 5px`}
              data-testid="mention-icon"
            />
          </Flex>
          <SupportsTouch>
            {supportsTouch => (
              <Tooltip
                content={<span>{shortcutSuggestionTooltipContent()}</span>}
                placement="top"
                disabled={supportsTouch || !commentBody}>
                <SendButton
                  type="submit"
                  icon={SendMessageIcon}
                  size={26}
                  disabled={!commentBody}
                />
              </Tooltip>
            )}
          </SupportsTouch>
        </Flex>
      </StyledForm>
    </Formik>
  )
}

export default CommentInput
